// @ts-nocheck
import client from "../../utils/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ResourceReservationCreateMutationRequest, ResourceReservationCreateMutationResponse, ResourceReservationCreateHeaderParams } from "../models/ResourceReservationCreate";
import type { UseMutationOptions } from "@tanstack/react-query";

 type ResourceReservationCreateClient = typeof client<ResourceReservationCreateMutationResponse, never, ResourceReservationCreateMutationRequest>;
type ResourceReservationCreate = {
    data: ResourceReservationCreateMutationResponse;
    error: never;
    request: ResourceReservationCreateMutationRequest;
    pathParams: never;
    queryParams: never;
    headerParams: ResourceReservationCreateHeaderParams;
    response: ResourceReservationCreateMutationResponse;
    client: {
        parameters: Partial<Parameters<ResourceReservationCreateClient>[0]>;
        return: Awaited<ReturnType<ResourceReservationCreateClient>>;
    };
};
/**
 * @description Create a new resource reservation or multiple reservations based on the provided configuration.<br><br>Features:<br>- Single resource reservation: Create a reservation for one resource<br>- Multiple resource reservations: Create reservations for multiple resources simultaneously<br>- Recurring reservations: Support for creating recurring reservations with custom patterns<br>- Custom fields: Support for adding custom field values to reservations<br>- Attendee management: Add internal and external attendees with different roles<br>- Online meeting integration: Configure online meeting settings for virtual meetings<br><br>The system will validate:<br>- Resource availability during the requested time slots<br>- User permissions and access rights<br>- Time slot conflicts and overlaps<br>- Required fields and data format
 * @summary Create Resource Reservation
 * @link /api/v5/resource-reservations
 */
export function useResourceReservationCreate(options: {
    invalidationOnSuccess?: boolean;
    mutation?: UseMutationOptions<ResourceReservationCreate["response"], ResourceReservationCreate["error"], {
        headers?: ResourceReservationCreate["headerParams"];
        data?: ResourceReservationCreate["request"];
    }>;
    client?: ResourceReservationCreate["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    const queryClient = useQueryClient();
    let invalidationOnSuccessOption = options?.invalidationOnSuccess ?? true;
    return useMutation({
        mutationFn: async ({ headers, data }) => {
            const res = await client<ResourceReservationCreate["data"], ResourceReservationCreate["error"], ResourceReservationCreate["request"]>({
                method: "post",
                url: `/api/v5/resource-reservations`,
                data,
                headers: { ...headers, ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions,
        onSuccess: (...args) => {
            if (invalidationOnSuccessOption) {
                queryClient.invalidateQueries({
                    queryKey: ["api", "v5", "resource-reservations"]
                });
            }
            if (mutationOptions?.onSuccess)
                mutationOptions.onSuccess(...args);
        },
    });
}